import { FormFieldOverride } from "types/enums";

import { FormFieldsFormData } from "./formSchema";

type FormFieldData = App.Data.FormFieldData;
type GlobalFormFieldData = App.Data.GlobalFormFieldData;

export interface FormFieldOverrideValueMapping {
    formProperty: Extract<keyof FormFieldsFormData, string>;
    contextProperty: string;
}

export interface FormFieldOverrideContext {
    own_values: Record<string, unknown>;
    source_values: Record<string, unknown> | null;
    overridden_attributes: FormFieldOverride[];
}

export const FORM_FIELD_OVERRIDE_MAPPINGS: Record<FormFieldOverride, FormFieldOverrideValueMapping[]> = {
    [FormFieldOverride.VisibleDescription]: [
        { formProperty: "visible_description", contextProperty: "visible_description" },
    ],
    [FormFieldOverride.DocNote]: [{ formProperty: "doc_note", contextProperty: "doc_note" }],
    [FormFieldOverride.IsRequired]: [{ formProperty: "is_required", contextProperty: "is_required" }],
    [FormFieldOverride.IsHidden]: [{ formProperty: "is_hidden", contextProperty: "is_hidden" }],
    [FormFieldOverride.DefaultValue]: [{ formProperty: "default_value", contextProperty: "default_value" }],
    [FormFieldOverride.ExampleValue]: [{ formProperty: "example_value", contextProperty: "example_value" }],
    [FormFieldOverride.Placeholder]: [{ formProperty: "placeholder", contextProperty: "placeholder" }],
    [FormFieldOverride.NotShowIfSystemNameEquals]: [
        {
            formProperty: "not_show_if_system_name_equals",
            contextProperty: "not_show_if_system_name_equals",
        },
    ],
    [FormFieldOverride.ShowOnlyIfSystemNameEquals]: [
        {
            formProperty: "show_only_if_system_name_equals",
            contextProperty: "show_only_if_system_name_equals",
        },
    ],
    [FormFieldOverride.ValidationRegex]: [{ formProperty: "validation_regex", contextProperty: "validation_regex" }],
    [FormFieldOverride.FrontendFormat]: [{ formProperty: "frontend_format", contextProperty: "frontend_format" }],
    [FormFieldOverride.Range]: [
        { formProperty: "min", contextProperty: "min" },
        { formProperty: "max", contextProperty: "max" },
    ],
    [FormFieldOverride.FormFieldSelectItems]: [
        { formProperty: "formFieldSelectItems", contextProperty: FormFieldOverride.FormFieldSelectItems },
    ],
    [FormFieldOverride.FormFieldRadioValues]: [
        { formProperty: "formFieldRadioValues", contextProperty: FormFieldOverride.FormFieldRadioValues },
    ],
    [FormFieldOverride.ValidationScenarios]: [
        { formProperty: "validation_scenarios", contextProperty: FormFieldOverride.ValidationScenarios },
    ],
    [FormFieldOverride.ModificationScenarios]: [
        { formProperty: "modification_scenarios", contextProperty: FormFieldOverride.ModificationScenarios },
    ],
    [FormFieldOverride.AutocompleteScenarios]: [
        { formProperty: "autocomplete_scenarios", contextProperty: FormFieldOverride.AutocompleteScenarios },
    ],
    [FormFieldOverride.DataModificationAccessor]: [
        { formProperty: "data_modification_accessor", contextProperty: "data_modification_accessor" },
    ],
    [FormFieldOverride.AutocompleteAccessor]: [
        { formProperty: "autocomplete_accessor", contextProperty: "autocomplete_accessor" },
    ],
};

export const GLOBAL_UNSUPPORTED_OVERRIDES = new Set<FormFieldOverride>([
    FormFieldOverride.FormFieldSelectItems,
    FormFieldOverride.FormFieldRadioValues,
    FormFieldOverride.ValidationScenarios,
    FormFieldOverride.ModificationScenarios,
    FormFieldOverride.AutocompleteScenarios,
    FormFieldOverride.DataModificationAccessor,
    FormFieldOverride.AutocompleteAccessor,
]);

const scenarioOverrides = new Set<FormFieldOverride>([
    FormFieldOverride.ValidationScenarios,
    FormFieldOverride.ModificationScenarios,
    FormFieldOverride.AutocompleteScenarios,
]);

export const normalizeOverrideValue = (override: FormFieldOverride, value: unknown): unknown => {
    if (!scenarioOverrides.has(override) || !Array.isArray(value)) return value;

    return value
        .filter((item): item is { id: string } => Boolean(item && typeof item === "object" && "id" in item))
        .map((item, index) => ({ id: String(item.id), order: index }));
};

export const buildOwnOverrideValues = (formField: FormFieldsFormData): Record<string, unknown> => {
    const values: Record<string, unknown> = {
        system_name: formField.system_name,
        type: formField.type,
    };

    Object.values(FORM_FIELD_OVERRIDE_MAPPINGS).forEach((mappings) => {
        mappings.forEach(({ formProperty, contextProperty }) => {
            values[contextProperty] = formField[formProperty];
        });
    });

    return values;
};

const buildScalarSourceValues = (field: FormFieldData | GlobalFormFieldData): Record<string, unknown> => ({
    system_name: field.system_name,
    type: field.type,
    visible_description: field.visible_description,
    doc_note: field.doc_note,
    is_required: field.is_required,
    is_hidden: field.is_hidden,
    default_value: field.default_value,
    example_value: field.example_value,
    placeholder: field.placeholder,
    not_show_if_system_name_equals: field.not_show_if_system_name_equals,
    show_only_if_system_name_equals: field.show_only_if_system_name_equals,
    validation_regex: field.validation_regex,
    frontend_format: field.frontend_format,
    min: field.min,
    max: field.max,
});

const mapScenarios = (scenarios: FormFieldData["validationScenarios"]): Array<{ id: string; order: number }> =>
    (scenarios || []).filter((scenario) => scenario.id).map((scenario, order) => ({ id: scenario.id || "", order }));

export const buildLocalSourceValues = (field: FormFieldData): Record<string, unknown> => ({
    ...buildScalarSourceValues(field),
    data_modification_accessor: field.data_modification_accessor,
    autocomplete_accessor: field.autocomplete_accessor,
    [FormFieldOverride.FormFieldSelectItems]: field.formFieldSelectItems || [],
    [FormFieldOverride.FormFieldRadioValues]: field.formFieldRadioValues || [],
    [FormFieldOverride.ValidationScenarios]: mapScenarios(field.validationScenarios),
    [FormFieldOverride.ModificationScenarios]: mapScenarios(field.modificationScenarios),
    [FormFieldOverride.AutocompleteScenarios]: mapScenarios(field.autocompleteScenarios),
});

export const buildGlobalSourceValues = (field: GlobalFormFieldData): Record<string, unknown> =>
    buildScalarSourceValues(field);
